import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/axios';
import { useSubscription } from '../contexts/SubscriptionContext';

const Spinner = () => (
  <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
  </svg>
);

function todayYMD() {
  return new Date().toISOString().slice(0, 10);
}

function AddLeaveModal({ open, onClose, employees, onAdded }) {
  const { t } = useTranslation('team');
  const { t: tCommon } = useTranslation('common');
  const { isGated } = useSubscription();
  const [employeeId, setEmployeeId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const selectRef = useRef(null);

  const activeEmployees = (employees || []).filter(e => e.isActive);

  useEffect(() => {
    if (!open) return;
    setEmployeeId('');
    setStartDate(todayYMD());
    setEndDate(todayYMD());
    setError('');
    setSaving(false);
    setTimeout(() => selectRef.current?.focus(), 50);
  }, [open]);

  const isValid = employeeId && startDate && endDate && endDate >= startDate;

  const handleSubmit = async () => {
    if (!isValid || isGated) return;
    setSaving(true);
    setError('');
    try {
      const res = await apiClient.post('/api/leave-requests', {
        employeeId: Number(employeeId),
        startDate,
        endDate,
      });
      onAdded(res.data);
    } catch (err) {
      setError(err.response?.data?.message || t('leave.errors.actionFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="al-modal-title"
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-orange-100 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <h2 id="al-modal-title" className="text-lg font-semibold text-gray-900">
            {t('leave.modal.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="ml-4 text-gray-400 hover:text-gray-600 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-orange-200 rounded"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          <p className="text-xs text-gray-500 -mt-1">{t('leave.modal.hint')}</p>

          <div>
            <label htmlFor="al-employee" className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('leave.modal.fields.staff')} <span className="text-red-500" aria-hidden="true">*</span>
            </label>
            <select
              ref={selectRef}
              id="al-employee"
              value={employeeId}
              onChange={e => { setEmployeeId(e.target.value); setError(''); }}
              disabled={saving}
              className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-900 outline-none transition focus:ring-2 focus:ring-orange-200 focus:border-orange-500 disabled:opacity-60"
            >
              <option value="">{t('leave.modal.fields.staffPlaceholder')}</option>
              {activeEmployees.map(e => (
                <option key={e.id} value={e.id}>{e.fullName}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="al-start" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('leave.modal.fields.startDate')} <span className="text-red-500" aria-hidden="true">*</span>
              </label>
              <input
                id="al-start"
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                disabled={saving}
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-900 outline-none transition focus:ring-2 focus:ring-orange-200 focus:border-orange-500 disabled:opacity-60"
              />
            </div>
            <div>
              <label htmlFor="al-end" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('leave.modal.fields.endDate')} <span className="text-red-500" aria-hidden="true">*</span>
              </label>
              <input
                id="al-end"
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={e => setEndDate(e.target.value)}
                disabled={saving}
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-900 outline-none transition focus:ring-2 focus:ring-orange-200 focus:border-orange-500 disabled:opacity-60"
              />
            </div>
          </div>

          {isGated && (
            <p className="text-xs text-red-600" role="alert">{tCommon('gatedAction.tooltip')}</p>
          )}

          {error && (
            <p className="text-xs text-red-600" role="alert">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
          >
            {t('leave.modal.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isValid || saving || isGated}
            title={isGated ? tCommon('gatedAction.tooltip') : undefined}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 px-5 py-2 text-sm font-semibold text-white shadow-md shadow-orange-200 transition hover:from-orange-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving && <Spinner />}
            {t('leave.modal.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddLeaveModal;
